// TODO(follow-up): Split this module into focused files under this folder:
//   shared/types.ts
//   symmetric/block.ts
//   symmetric/card-fields.ts
//   symmetric/card-style.ts
//   symmetric/carousel.ts
//   symmetric/layout.ts
//   symmetric/readers.ts
import type { CSSProperties, ReactNode } from 'react';
import { v7 as uuidv7 } from 'uuid';

import { getVariantEntry } from '@/lib/blocks/collection/variant-registry';
import { deriveContentToneFromColor, resolveColorVariable } from '@/lib/derive-content-tone';
import { cloneValue, readBoolean, readRecord, readString } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';
import type { PageBlock } from '@/types/block';
import type { ColorSettings } from '@/types/shared';

export type CollectionGap = 'sm' | 'md' | 'lg';
export type CollectionCardPadding = CollectionGap | 'none';
export type CollectionColumnCount = 1 | 2 | 3 | 4;
export type CollectionImagePosition = 'top' | 'left' | 'right' | 'background';
export interface CollectionColumns {
  mobile: CollectionColumnCount;
  tablet: CollectionColumnCount;
  desktop: CollectionColumnCount;
}

export interface CollectionLayoutConfig {
  columns: CollectionColumns;
  gap: CollectionGap;
}

// Symmetric collection types

export type CollectionCardFieldKind = 'text' | 'image' | 'icon' | 'button';
export type CollectionCardAlign = 'start' | 'center' | 'end';

export interface CollectionCardField {
  slot: string;
  kind: CollectionCardFieldKind;
  label: string;
  enabled: boolean;
  /** ID of the child block record in the card_template. Present when derived from DB blocks. */
  blockId?: string;
  /** Raw props from the card_template child block (e.g. aspect, fit for image fields). */
  blockProps?: Record<string, unknown>;
}

export type CollectionCardJustify = 'start' | 'center' | 'end';
export type CollectionCardContentPanel = 'dark' | (string & {});

export interface CollectionCardConfig {
  gap: CollectionGap;
  align: CollectionCardAlign;
  justify?: CollectionCardJustify;
  fields: CollectionCardField[];
  minHeight?: number;
  sourceMode?: 'dynamic' | 'static';
  contentPanel?: CollectionCardContentPanel;
  contentPadding?: boolean;
}

export interface CollectionItem {
  id: string;
  [slot: string]: unknown;
}

export interface SymmetricCollectionProps {
  variant: string;
  variantProps: Record<string, Record<string, unknown>>;
  items: CollectionItem[];
}

const COLLECTION_GAP_CLASSNAMES: Record<CollectionGap, string> = {
  sm: 'gap-2',
  md: 'gap-6',
  lg: 'gap-8',
};

const MOBILE_COLUMN_CLASSNAMES: Record<CollectionColumnCount, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

const TABLET_COLUMN_CLASSNAMES: Record<CollectionColumnCount, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
};

const DESKTOP_COLUMN_CLASSNAMES: Record<CollectionColumnCount, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
};

function readColumnCount(value: unknown, fallback: CollectionColumnCount): CollectionColumnCount {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }

  const normalized = Math.trunc(value);
  if (normalized < 1 || normalized > 4) {
    return fallback;
  }

  return normalized as CollectionColumnCount;
}

function readVariantRecord(
  props: Record<string, unknown>,
  key: string,
): Record<string, unknown> | undefined {
  return readRecord(props[key]);
}

function readVariantString(props: Record<string, unknown>, key: string): string | undefined {
  return readString(props[key]);
}

function readVariantBoolean(props: Record<string, unknown>, key: string): boolean | undefined {
  return readBoolean(props[key]);
}

export function isCollectionBlock(block: PageBlock): boolean {
  return block.blockType === 'collection';
}

function readCollectionItems(value: unknown): CollectionItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is CollectionItem =>
      item !== null && typeof item === 'object' && typeof item.id === 'string',
  );
}

export function resolveSymmetricCollectionProps(block: PageBlock): SymmetricCollectionProps {
  const props = block.props || {};
  const variant = readString(props.variant) ?? '';
  const variantProps =
    (readRecord(props.variantProps) as Record<string, Record<string, unknown>>) ?? {};

  return {
    variant,
    variantProps,
    items: readCollectionItems(props.items),
  };
}

// Card template block helpers

const CARD_FIELD_KIND_MAP: Record<string, CollectionCardFieldKind> = {
  image: 'image',
  icon: 'icon',
  button: 'button',
  text: 'text',
};

export const MEDIA_FIELD_KINDS: readonly CollectionCardFieldKind[] = ['image', 'icon'];

export function isMediaFieldKind(kind: CollectionCardFieldKind): boolean {
  return MEDIA_FIELD_KINDS.includes(kind);
}

function blockTypeToFieldKind(blockType: string): CollectionCardFieldKind {
  return CARD_FIELD_KIND_MAP[blockType] ?? 'text';
}

function formatSlotLabel(slot: string): string {
  if (slot === 'body') return 'Description';
  return slot.charAt(0).toUpperCase() + slot.slice(1).replace(/_/g, ' ');
}

export const CARD_ALIGN_VALUES: readonly CollectionCardAlign[] = ['start', 'center', 'end'];

/**
 * Derive the CollectionCardConfig from the card_template block and its children.
 * Children are sorted by position; enabled state comes from block.visible.
 */
export function resolveCardConfigFromTemplate(
  templateBlock: PageBlock,
  templateChildren: PageBlock[],
  variantSpecificProps?: Record<string, unknown>,
): CollectionCardConfig {
  const tProps = (templateBlock.props as Record<string, unknown>) ?? {};
  const gapStr = readString(tProps.gap);
  const gap: CollectionGap = gapStr === 'sm' || gapStr === 'lg' ? gapStr : 'md';
  const alignStr = readString(variantSpecificProps?.align);
  const align: CollectionCardAlign = (CARD_ALIGN_VALUES as readonly string[]).includes(
    alignStr ?? '',
  )
    ? (alignStr as CollectionCardAlign)
    : 'start';

  const sortedChildren = [...templateChildren].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );
  const fields: CollectionCardField[] = sortedChildren.map((child) => {
    const cProps = (child.props as Record<string, unknown>) ?? {};
    const slot = readString(cProps.slot) ?? '';
    const labelFromProps = readString(cProps.label);
    return {
      slot,
      kind: blockTypeToFieldKind(child.blockType),
      label: labelFromProps ?? formatSlotLabel(slot),
      enabled: child.visible !== false,
      blockId: child.id,
      blockProps: cProps,
    };
  });

  const rawMinHeight = variantSpecificProps?.minHeight;
  const minHeight = typeof rawMinHeight === 'number' && rawMinHeight > 0 ? rawMinHeight : undefined;

  const rawJustify = readString(variantSpecificProps?.justify);
  const justify: CollectionCardJustify | undefined =
    rawJustify === 'start' || rawJustify === 'center' || rawJustify === 'end'
      ? rawJustify
      : undefined;

  const rawContentPanel = readString(variantSpecificProps?.contentPanel);
  const contentPanel: CollectionCardContentPanel | undefined =
    rawContentPanel && rawContentPanel.length > 0 ? rawContentPanel : undefined;

  const contentPadding = readBoolean(variantSpecificProps?.contentPadding) ?? false;

  return { gap, align, justify, fields, minHeight, contentPanel, contentPadding };
}

function placeholderTextForField(field: CollectionCardField): string {
  const label = field.label.trim();
  const slot = field.slot.toLowerCase();
  if (slot.includes('title') || slot.includes('heading') || slot.includes('name')) {
    return `<h3>${label || 'Title'}</h3>`;
  }
  if (
    slot.includes('body') ||
    slot.includes('description') ||
    slot.includes('content') ||
    slot.includes('text')
  ) {
    return `<p>${label || 'Description'} goes here.</p>`;
  }
  return label || 'Text';
}

export function injectPlaceholdersForField(
  items: CollectionItem[],
  field: CollectionCardField,
): CollectionItem[] {
  return items.map((item) => {
    if (item[field.slot] != null && item[field.slot] !== '') return item;
    const patched = { ...item };
    if (field.kind === 'button') {
      patched[field.slot] = {
        text: 'Learn More',
        link: '',
        style: 'primary',
        linkType: 'external',
      };
    } else if (isMediaFieldKind(field.kind)) {
      patched[field.slot] = null;
    } else {
      patched[field.slot] = placeholderTextForField(field);
    }
    return patched;
  });
}

export function createEmptyCollectionItem(fields: CollectionCardField[]): CollectionItem {
  const item: CollectionItem = { id: uuidv7() };
  for (const field of fields) {
    if (field.kind === 'button') {
      item[field.slot] = { text: 'Learn More', link: '', style: 'primary', linkType: 'external' };
    } else if (isMediaFieldKind(field.kind)) {
      item[field.slot] = null;
    } else {
      item[field.slot] = placeholderTextForField(field);
    }
  }
  return item;
}

export function resolveSymmetricCarouselDisplay(
  variantSpecificProps: Record<string, unknown>,
): SymmetricCarouselDisplay {
  const orientationStr = readVariantString(variantSpecificProps, 'orientation');
  const orientation: CarouselOrientation =
    orientationStr === 'vertical' ? 'vertical' : 'horizontal';
  const ipvRecord = readVariantRecord(variantSpecificProps, 'itemsPerView');
  const itemsPerView = (() => {
    if (orientation === 'vertical') {
      return { mobile: 3, tablet: 3, desktop: 3 };
    }

    const toIPV = (v: unknown, fallback: number): number => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.min(6, Math.max(1, Math.trunc(n))) : fallback;
    };
    const desktop = toIPV(ipvRecord?.desktop, 3);
    const tablet = toIPV(ipvRecord?.tablet, 2);
    const mobile = toIPV(ipvRecord?.mobile, 1);
    return { mobile, tablet, desktop };
  })();
  const gapStr = readVariantString(variantSpecificProps, 'gap');
  const gap: CollectionGap = gapStr === 'sm' || gapStr === 'lg' ? gapStr : 'md';
  const heightStr = readVariantString(variantSpecificProps, 'verticalHeight');
  const verticalHeight: CarouselVerticalHeight =
    heightStr === 'sm' || heightStr === 'lg' ? heightStr : 'md';
  const navPositionStr = readVariantString(variantSpecificProps, 'navPosition');
  const VALID_NAV_POSITIONS: CarouselNavPosition[] = [
    'sides',
    'sides-inset',
    'bottom-center',
    'top-right',
    'bottom-left',
  ];
  const navPosition: CarouselNavPosition = VALID_NAV_POSITIONS.includes(
    navPositionStr as CarouselNavPosition,
  )
    ? (navPositionStr as CarouselNavPosition)
    : 'sides';

  const navButtonStyleStr = readVariantString(variantSpecificProps, 'navButtonStyle');
  const navButtonStyle: CarouselNavButtonStyle =
    navButtonStyleStr === 'solid' ? 'solid' : 'outline';

  return {
    itemsPerView,
    gap,
    arrows: readVariantBoolean(variantSpecificProps, 'arrows') ?? true,
    autoScroll: readVariantBoolean(variantSpecificProps, 'autoScroll') ?? false,
    orientation,
    verticalHeight,
    navPosition,
    navButtonStyle,
  };
}

const ALTERNATING_ROW_GAP_CLASSNAMES: Record<CollectionGap, string> = {
  sm: 'gap-4',
  md: 'gap-6',
  lg: 'gap-8',
};

function readGapProp(variantSpecificProps: Record<string, unknown>): CollectionGap {
  const gapStr = readVariantString(variantSpecificProps, 'gap');
  return gapStr === 'sm' || gapStr === 'lg' ? gapStr : 'md';
}

export function resolveSymmetricAlternatingGap(
  variantSpecificProps: Record<string, unknown>,
): CollectionGap {
  return readGapProp(variantSpecificProps);
}

export function getAlternatingContainerClassName(gap: CollectionGap): string {
  return cn('flex flex-col', ALTERNATING_ROW_GAP_CLASSNAMES[gap]);
}

export function resolveSymmetricGridLayout(
  variantSpecificProps: Record<string, unknown>,
): CollectionLayoutConfig {
  const columnsRecord = readVariantRecord(variantSpecificProps, 'columns');
  const desktop = readColumnCount(Number(columnsRecord?.desktop), 3);
  const tablet = readColumnCount(
    Number(columnsRecord?.tablet),
    Math.min(desktop, 2) as CollectionColumnCount,
  );
  const mobile = readColumnCount(Number(columnsRecord?.mobile), 1);
  const gapStr = readVariantString(variantSpecificProps, 'gap');
  const gap: CollectionGap = gapStr === 'sm' || gapStr === 'lg' ? gapStr : 'md';
  return { columns: { mobile, tablet, desktop }, gap };
}

/**
 * Layout used for editor chrome and parent DnD metadata. Matches display: reads
 * `variant` + `variantProps[variant]` (not legacy top-level `columns` / `gap` on the block).
 */
export function resolveCollectionLayout(block: PageBlock): CollectionLayoutConfig {
  const symProps = resolveSymmetricCollectionProps(block);
  const variantEntry = getVariantEntry(symProps.variant);
  const variantSpecific = (symProps.variantProps[symProps.variant] ??
    variantEntry?.defaultProps ??
    {}) as Record<string, unknown>;

  const renderer = variantEntry?.renderer;

  if (renderer === 'grid' || renderer === 'accordion') {
    return resolveSymmetricGridLayout(variantSpecific);
  }

  if (renderer === 'carousel') {
    const display = resolveSymmetricCarouselDisplay(variantSpecific);
    return {
      columns: { mobile: 1, tablet: 1, desktop: 1 },
      gap: display.gap,
    };
  }

  if (renderer === 'alternating') {
    return {
      columns: { mobile: 1, tablet: 1, desktop: 1 },
      gap: resolveSymmetricAlternatingGap(variantSpecific),
    };
  }

  const columnsRecord = readRecord(block.props?.columns);
  const desktop = readColumnCount(columnsRecord?.desktop, 3);
  const tablet = readColumnCount(
    columnsRecord?.tablet,
    Math.min(desktop, 2) as CollectionColumnCount,
  );
  const mobile = readColumnCount(columnsRecord?.mobile, 1);
  const gapValue = readString(block.props?.gap);
  const gap: CollectionGap = gapValue === 'sm' || gapValue === 'lg' ? gapValue : 'md';

  return {
    columns: {
      mobile,
      tablet,
      desktop,
    },
    gap,
  };
}

export function resolveSymmetricPadding(
  variantSpecificProps: Record<string, unknown>,
  defaultPadding: CollectionCardPadding = 'md',
): CollectionCardPadding {
  const p = readVariantString(variantSpecificProps, 'padding');
  if (p === 'none') return 'none';
  if (p === 'sm' || p === 'md' || p === 'lg') return p;
  return defaultPadding;
}

const CARD_PADDING_CLASSNAMES: Record<CollectionGap, string> = {
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-7',
};

const ACCORDION_CONTAINER_PADDING_CLASSNAMES: Record<CollectionGap, string> = {
  sm: 'px-3',
  md: 'px-5',
  lg: 'px-7',
};

export function getCollectionCardPaddingClassName(padding: CollectionCardPadding): string {
  if (padding === 'none') return '';
  return CARD_PADDING_CLASSNAMES[padding];
}

export function getAccordionContainerPaddingClassName(padding: CollectionCardPadding): string {
  if (padding === 'none') return '';
  return ACCORDION_CONTAINER_PADDING_CLASSNAMES[padding];
}

export function getAccordionSingleGapClassName(gap: CollectionGap): string {
  return cn('flex flex-col', COLLECTION_GAP_CLASSNAMES[gap]);
}

export function resolveCarouselHasHeadingRow(
  heading: ReactNode,
  showArrows: boolean,
  navPosition: CarouselNavPosition,
): boolean {
  return Boolean(heading) && showArrows && navPosition === 'top-right';
}

export function getCollectionContainerClassName(config: CollectionLayoutConfig): string {
  return cn(
    'grid',
    COLLECTION_GAP_CLASSNAMES[config.gap],
    MOBILE_COLUMN_CLASSNAMES[config.columns.mobile],
    TABLET_COLUMN_CLASSNAMES[config.columns.tablet],
    DESKTOP_COLUMN_CLASSNAMES[config.columns.desktop],
  );
}

export { cloneValue };

export function findCardTemplateBlock(childrenOfCollection: PageBlock[]): PageBlock | null {
  return (
    childrenOfCollection.find(
      (b) =>
        b.blockType === 'group' && (b.props as Record<string, unknown>)?.role === 'card_template',
    ) ?? null
  );
}

// Carousel item class helpers

export type CarouselItemsPerView = 1 | 2 | 3 | 4 | 5 | 6;

export type CarouselOrientation = 'horizontal' | 'vertical';
export type CarouselVerticalHeight = 'sm' | 'md' | 'lg';
export type CarouselNavPosition =
  | 'sides'
  | 'sides-inset'
  | 'bottom-center'
  | 'top-right'
  | 'bottom-left';
export type CarouselNavButtonStyle = 'outline' | 'solid';

export interface SymmetricCarouselDisplay {
  itemsPerView: { mobile: number; tablet: number; desktop: number };
  gap: CollectionGap;
  arrows: boolean;
  autoScroll: boolean;
  orientation: CarouselOrientation;
  verticalHeight: CarouselVerticalHeight;
  navPosition: CarouselNavPosition;
  navButtonStyle: CarouselNavButtonStyle;
}

const VERTICAL_HEIGHT_CLASSNAMES: Record<CarouselVerticalHeight, string> = {
  sm: 'h-72',
  md: 'h-[520px]',
  lg: 'h-[760px]',
};

export function getVerticalCarouselHeightClassName(height: CarouselVerticalHeight): string {
  return VERTICAL_HEIGHT_CLASSNAMES[height];
}

export interface CarouselDisplayConfig {
  itemsPerView: {
    mobile: CarouselItemsPerView;
    tablet: CarouselItemsPerView;
    desktop: CarouselItemsPerView;
  };
  gap: CollectionGap;
  arrows: boolean;
  orientation: CarouselOrientation;
  navPosition: CarouselNavPosition;
  navButtonStyle: CarouselNavButtonStyle;
}

const CAROUSEL_H_GAP_CLASSNAMES: Record<CollectionGap, string> = {
  sm: 'pl-4',
  md: 'pl-6',
  lg: 'pl-8',
};

const CAROUSEL_V_GAP_CLASSNAMES: Record<CollectionGap, string> = {
  sm: 'pt-4',
  md: 'pt-6',
  lg: 'pt-8',
};

const CAROUSEL_MOBILE_ITEM_CLASSNAMES: Record<CarouselItemsPerView, string> = {
  1: 'basis-full',
  2: 'basis-1/2',
  3: 'basis-1/3',
  4: 'basis-1/4',
  5: 'basis-1/5',
  6: 'basis-1/6',
};

const CAROUSEL_TABLET_ITEM_CLASSNAMES: Record<CarouselItemsPerView, string> = {
  1: 'md:basis-full',
  2: 'md:basis-1/2',
  3: 'md:basis-1/3',
  4: 'md:basis-1/4',
  5: 'md:basis-1/5',
  6: 'md:basis-1/6',
};

const CAROUSEL_DESKTOP_ITEM_CLASSNAMES: Record<CarouselItemsPerView, string> = {
  1: 'lg:basis-full',
  2: 'lg:basis-1/2',
  3: 'lg:basis-1/3',
  4: 'lg:basis-1/4',
  5: 'lg:basis-1/5',
  6: 'lg:basis-1/6',
};

export function getCarouselItemClassName(config: CarouselDisplayConfig): string {
  if (config.orientation === 'vertical') {
    return cn(
      CAROUSEL_V_GAP_CLASSNAMES[config.gap],
      CAROUSEL_MOBILE_ITEM_CLASSNAMES[config.itemsPerView.mobile],
      CAROUSEL_TABLET_ITEM_CLASSNAMES[config.itemsPerView.tablet],
      CAROUSEL_DESKTOP_ITEM_CLASSNAMES[config.itemsPerView.desktop],
    );
  }
  return cn(
    CAROUSEL_H_GAP_CLASSNAMES[config.gap],
    CAROUSEL_MOBILE_ITEM_CLASSNAMES[config.itemsPerView.mobile],
    CAROUSEL_TABLET_ITEM_CLASSNAMES[config.itemsPerView.tablet],
    CAROUSEL_DESKTOP_ITEM_CLASSNAMES[config.itemsPerView.desktop],
  );
}

// Carousel content

const CAROUSEL_H_CONTENT_CLASSNAMES: Record<CollectionGap, string> = {
  sm: '-ml-4',
  md: '-ml-6',
  lg: '-ml-8',
};

const CAROUSEL_V_CONTENT_CLASSNAMES: Record<CollectionGap, string> = {
  sm: '-mt-4',
  md: '-mt-6',
  lg: '-mt-8',
};

export function getCarouselContentClassName(
  gap: CollectionGap,
  orientation: CarouselOrientation = 'horizontal',
): string {
  return orientation === 'vertical'
    ? CAROUSEL_V_CONTENT_CLASSNAMES[gap]
    : CAROUSEL_H_CONTENT_CLASSNAMES[gap];
}

/** Keep copy clear of side arrows without shrinking slide backgrounds. */
export function getCarouselContentPaddingClassName(
  display: Pick<SymmetricCarouselDisplay, 'navPosition' | 'orientation' | 'arrows' | 'autoScroll'>,
): string {
  if (display.orientation === 'vertical' || !display.arrows || display.autoScroll) return '';

  switch (display.navPosition) {
    case 'sides-inset':
      return 'px-12 md:px-16';
    case 'sides':
      return 'px-8 md:px-6';
    default:
      return '';
  }
}

// Card content gap

const CARD_CONTENT_GAP_CLASSNAMES: Record<CollectionGap, string> = {
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-4',
};

export function getCardContentGapClassName(gap: CollectionGap): string {
  return CARD_CONTENT_GAP_CLASSNAMES[gap];
}

export function getAlignmentClassName(align: CollectionCardAlign): string {
  if (align === 'center') return 'justify-center text-center';
  if (align === 'end') return 'justify-end text-right';
  return '';
}

// Card field helpers

export function getEnabledFields(fields: CollectionCardField[]): CollectionCardField[] {
  return fields.filter((f) => f.enabled);
}

export function getImageField(fields: CollectionCardField[]): CollectionCardField | null {
  return fields.find((f) => f.kind === 'image') ?? null;
}

export function getIconField(fields: CollectionCardField[]): CollectionCardField | null {
  return fields.find((f) => f.kind === 'icon') ?? null;
}

export interface MediaFields {
  image: CollectionCardField | null;
  icon: CollectionCardField | null;
}

export function getMediaFields(fields: CollectionCardField[]): MediaFields {
  return { image: getImageField(fields), icon: getIconField(fields) };
}

export function getActiveMediaField(fields: CollectionCardField[]): CollectionCardField | null {
  const enabledImage = fields.find((f) => f.kind === 'image' && f.enabled) ?? null;
  if (enabledImage) return enabledImage;
  return fields.find((f) => f.kind === 'icon' && f.enabled) ?? null;
}

export function getContentFields(fields: CollectionCardField[]): CollectionCardField[] {
  return fields.filter((f) => !isMediaFieldKind(f.kind));
}

const ICON_SETTING_KEYS = ['size', 'color', 'backgroundShape', 'backgroundColor'] as const;

export function mergeIconBlockProps(
  field: CollectionCardField,
  itemRecord: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...(field.blockProps ?? {}) };
  for (const key of ICON_SETTING_KEYS) {
    if (itemRecord[key] !== undefined) merged[key] = itemRecord[key];
  }
  return merged;
}

export function getEnabledTextFields(fields: CollectionCardField[]): CollectionCardField[] {
  return fields.filter((f) => f.enabled && f.kind === 'text');
}

export function resolveAccordionFields(
  fields: CollectionCardField[],
  preferredQuestionSlot?: string,
): {
  questionField: CollectionCardField | undefined;
  bodyFields: CollectionCardField[];
} {
  const enabledTextFields = getEnabledTextFields(fields);
  const questionField = preferredQuestionSlot
    ? (enabledTextFields.find((f) => f.slot === preferredQuestionSlot) ?? enabledTextFields[0])
    : enabledTextFields[0];
  const bodyFields = fields.filter(
    (f) => f.enabled && !isMediaFieldKind(f.kind) && f.slot !== questionField?.slot,
  );
  return { questionField, bodyFields };
}

// Auto-scroll helpers

/**
 * Repeat items until total count fills at least 2 full viewports.
 * Prevents a visible gap/snap at the loop point when item count < itemsPerView.
 */
export function repeatItemsForAutoScroll<T>(items: T[], itemsPerView: number, minCopies = 2): T[] {
  if (items.length === 0) return items;
  const copies = Math.max(minCopies, Math.ceil((itemsPerView * 2) / items.length));
  return Array.from({ length: copies }, () => items).flat();
}

// Card style from variantSpecific

export function getCardBgClassName(
  style: CSSProperties | undefined,
  colors?: Partial<ColorSettings> | null,
): string {
  const rawBgColor = style?.backgroundColor;
  if (typeof rawBgColor !== 'string' || !rawBgColor) return '';
  const bgColor = colors ? (resolveColorVariable(rawBgColor, colors) ?? rawBgColor) : rawBgColor;
  const cardTextColor = colors?.cardText;
  const tone = deriveContentToneFromColor(bgColor, cardTextColor) ?? 'siteText';
  if (tone === 'light') {
    return 'text-white [&_.prose]:prose-invert [&_.prose]:!text-white [&_.prose_*]:!text-inherit';
  }
  if (tone === 'dark') {
    return 'text-black [&_.prose]:!text-black [&_.prose_*]:!text-inherit';
  }
  return 'text-card-foreground [&_.prose]:!text-card-foreground [&_.prose_*]:!text-inherit';
}

export function buildCardStyle(variantSpecific: Record<string, unknown>): {
  style: CSSProperties | undefined;
  hasCardStyling: boolean;
} {
  const borderColor = readString(variantSpecific.borderColor);
  const bgColor = readString(variantSpecific.backgroundColor);
  if (!borderColor && !bgColor) return { style: undefined, hasCardStyling: false };
  const style: CSSProperties = {};
  if (borderColor) style.border = `1px solid ${borderColor}`;
  if (bgColor) style.backgroundColor = bgColor;
  style.borderRadius = '0.5rem';
  return { style, hasCardStyling: true };
}
